import { useEffect, useRef, useState } from 'react';
import { config } from '../../config';
import type { ServerStatus } from '../../net/serverClient';
import {
  accountRequest,
  leaderboard,
  requestError,
  saveSession,
  type Account,
} from '../../net/accounts';
import { serverHost, serverWarning } from '../../net/serverOverride';
import { refreshGameLibrary, clearRemoteGameLibrary } from '../../editor/gameLibrary';
import { formatWins } from './wins';

type Mode = 'login' | 'register' | 'forgot' | 'verify' | 'reset';
export function AccountDialog({
  onBack,
  initialMode,
}: {
  onBack: () => void;
  initialMode: 'login' | 'register';
}) {
  const dialog = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    dialog.current?.showModal();
  }, []);
  return (
    <dialog
      ref={dialog}
      className="creation-dialog account-dialog"
      aria-label="Account"
      onCancel={(e) => {
        e.preventDefault();
        onBack();
      }}
    >
      <AccountPanel onBack={onBack} initialMode={initialMode} />
    </dialog>
  );
}
export function AccountPanel({
  onBack,
  initialMode = 'login',
}: {
  onBack: () => void;
  initialMode?: 'login' | 'register';
}) {
  const [link] = useState(() => new URLSearchParams(location.hash.slice(1)));
  const [mode, setMode] = useState<Mode>(() =>
    link.get('account') === 'verify'
      ? 'verify'
      : link.get('account') === 'reset'
        ? 'reset'
        : initialMode,
  );
  const [user, setUser] = useState<Account | null>(null);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!link.has('token'))
      void accountRequest<{ user: Account | null }>('account')
        .then((result) => setUser(result.user))
        .catch(() => {});
    // Fragments are not sent to the web server. Remove them from history once captured.
    if (link.has('token')) history.replaceState(null, '', location.pathname + location.search);
  }, [link]);
  const change = (next: Mode) => {
    setMode(next);
    setMessage('');
    setError('');
  };
  async function submit(form: HTMLFormElement) {
    setBusy(true);
    setError('');
    setMessage('');
    const data = Object.fromEntries(new FormData(form));
    try {
      if (mode === 'login') {
        const result = await accountRequest<{ token: string; user: Account }>('login', data);
        saveSession(result.token);
        clearRemoteGameLibrary();
        void refreshGameLibrary().catch(() => {});
        setUser(result.user);
        onBack();
      } else {
        const result = await accountRequest<{ message: string }>(mode, {
          ...data,
          token: link.get('token'),
        });
        setMessage(result.message);
        if (mode === 'verify' || mode === 'reset') setMode('login');
        form.reset();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Request failed');
    } finally {
      setBusy(false);
    }
  }
  return (
    <section className="account-panel">
      <h2>
        {user
          ? 'Your arena profile'
          : {
              login: 'Sign in',
              register: 'Create your account',
              forgot: 'Forgot password',
              verify: 'Confirm your email',
              reset: 'Choose a new password',
            }[mode]}
      </h2>
      {!user && <AccountServer />}
      {user ? (
        <>
          <p>
            Welcome, <strong>{user.name}</strong>.
          </p>
          <p>
            Elo <strong>{user.rating}</strong> · {user.games} rated games · {formatWins(user.wins)}{' '}
            wins
          </p>
          <p className="hint">
            Your identity is attached when you next connect to a room. Guest and AI games are
            unrated.
          </p>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              const data = Object.fromEntries(new FormData(e.currentTarget));
              setBusy(true);
              setError('');
              setMessage('');
              void accountRequest<{ user: Account }>('account', data)
                .then((r) => {
                  setUser(r.user);
                  setMessage('Profile updated.');
                  window.dispatchEvent(new Event('arena-account-changed'));
                })
                .catch((error) => setError(requestError(error)))
                .finally(() => setBusy(false));
            }}
          >
            <label>
              Email address
              <input value={user.email ?? ''} readOnly type="email" />
            </label>
            <label>
              First name
              <input
                name="firstName"
                defaultValue={user.firstName ?? ''}
                required
                maxLength={80}
                autoComplete="given-name"
              />
            </label>
            <label>
              Last name
              <input
                name="lastName"
                defaultValue={user.lastName ?? ''}
                required
                maxLength={80}
                autoComplete="family-name"
              />
            </label>
            <label>
              Gamer name / tag
              <input
                name="name"
                defaultValue={user.name}
                required
                minLength={2}
                maxLength={32}
                autoComplete="nickname"
              />
            </label>
            <button className="primary" disabled={busy}>
              Save profile
            </button>
          </form>
          <button
            disabled={busy}
            onClick={() => {
              setBusy(true);
              void accountRequest('logout', {})
                .then(() => {
                  saveSession('');
                  clearRemoteGameLibrary();
                  void refreshGameLibrary().catch(() => {});
                  setUser(null);
                })
                .catch((e) => setError(requestError(e)))
                .finally(() => setBusy(false));
            }}
          >
            Sign out
          </button>
        </>
      ) : (
        <form
          onSubmit={(event) => {
            event.preventDefault();
            void submit(event.currentTarget);
          }}
        >
          {mode === 'register' && (
            <>
              <label>
                First name
                <input name="firstName" required maxLength={80} autoComplete="given-name" />
              </label>
              <label>
                Last name
                <input name="lastName" required maxLength={80} autoComplete="family-name" />
              </label>
              <label>
                Gamer name / tag
                <input name="name" required minLength={2} maxLength={32} autoComplete="nickname" />
              </label>
            </>
          )}
          {['register', 'login', 'forgot'].includes(mode) && (
            <label>
              Email address
              <input name="email" type="email" required maxLength={254} autoComplete="email" />
            </label>
          )}
          {['register', 'login', 'reset'].includes(mode) && (
            <label>
              Password
              <input
                name="password"
                type="password"
                required
                minLength={10}
                maxLength={128}
                autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
              />
            </label>
          )}
          {mode === 'verify' && (
            <p>
              Press Confirm email to activate your account. The link works once and expires after 24
              hours.
            </p>
          )}
          {mode === 'register' && (
            <p className="hint">
              Your gamer tag and rating appear publicly. Your email and real name stay private.
              Confirm your email before signing in.
            </p>
          )}
          <button className="primary" disabled={busy}>
            {busy
              ? 'Please wait…'
              : {
                  login: 'Sign in',
                  register: 'Register',
                  forgot: 'Send reset link',
                  verify: 'Confirm email',
                  reset: 'Save new password',
                }[mode]}
          </button>
          <div className="account-links">
            <button
              type="button"
              disabled={busy}
              onClick={() => change(mode === 'register' ? 'login' : 'register')}
            >
              {mode === 'register' ? 'Already registered? Sign in' : 'Create an account'}
            </button>
            <button type="button" disabled={busy} onClick={() => change('forgot')}>
              Forgot password?
            </button>
          </div>
        </form>
      )}
      {message && <p role="status">{message}</p>}
      {error && <p role="alert">{error}</p>}
      <button onClick={onBack}>Back to menu</button>
    </section>
  );
}

/** Where the forms send what the player types, when a player needs telling (`serverWarning`). */
function AccountServer() {
  const warning = serverWarning(config.serverUrl, config.serverOverridden);
  if (warning === null) return null;
  return warning === 'none' ? (
    <p className="hint warn" data-testid="account-server">
      No game server is configured, so accounts are unavailable.
    </p>
  ) : (
    <p className="hint warn" data-testid="account-server">
      Custom game server from this page’s address: <strong>{serverHost(config.serverUrl)}</strong>.
      Your email and password go there.
    </p>
  );
}

/**
 * The rankings: on their own page with its explanation (`onBack`), or on the home screen. There
 * they are asked of an awake server only, once a minute at most, and a failure is one quiet line:
 * the first thing a newcomer read used to be the browser's error (client-tablets-ui:quality#5).
 */
export function LeaderboardPanel({
  onBack,
  server,
}: {
  onBack?: () => void;
  server?: ServerStatus;
}) {
  const home = !onBack;
  const ask = !home || server?.state === 'online';
  const [users, setUsers] = useState<Account[]>([]);
  const [page, setPage] = useState(0);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(ask);
  useEffect(() => {
    if (!ask) return;
    let alive = true;
    setLoading(true);
    void leaderboard()
      .then((found) => {
        if (alive) setUsers(found);
      })
      .catch((e) => {
        if (alive) setError(requestError(e));
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [ask]);
  if (!ask) {
    const line =
      server?.state === 'offline'
        ? 'Rankings are unavailable: the game server cannot be reached.'
        : server?.state === 'checking' || server?.state === 'waking'
          ? 'Rankings appear when the game server is awake.'
          : null;
    return line ? (
      <p className="hint rankings-note" data-testid="rankings-note">
        {line}
      </p>
    ) : null;
  }
  return (
    <section className="account-panel">
      <h2>Leaderboard</h2>
      {onBack && (
        <p className="hint">
          Elo starts at 1000. Beating a stronger opponent earns more points, and a draw counts as
          half a win for each side. Only games between distinct, confirmed human accounts count.
          Leaving, or losing your seat to the clock, counts as a loss. Someone still away with the
          AI in their seat when the game ends has lost it, and whoever stayed is rated on the
          result: a win only if they won it.
        </p>
      )}
      {loading ? (
        <p role="status">Loading rankings…</p>
      ) : error ? (
        home ? (
          <p className="hint rankings-note" data-testid="rankings-note">
            Rankings are unavailable right now.
          </p>
        ) : (
          <p role="alert">{error}</p>
        )
      ) : users.length === 0 ? (
        <p>No rated games yet. Be the first to take the field.</p>
      ) : (
        <table className="leaderboard">
          <thead>
            <tr>
              <th>Rank</th>
              <th>Player</th>
              <th>Elo</th>
              <th>Wins</th>
              <th>Games</th>
            </tr>
          </thead>
          <tbody>
            {users.slice(page * 10, page * 10 + 10).map((u, i) => (
              <tr key={u.id}>
                <td>{page * 10 + i + 1}</td>
                <td>{u.name}</td>
                <td>{u.rating}</td>
                <td>{formatWins(u.wins)}</td>
                <td>{u.games}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {users.length > 10 && (
        <nav className="menu-actions" aria-label="Leaderboard pages">
          <button disabled={page === 0} onClick={() => setPage((p) => p - 1)}>
            Previous
          </button>
          <span aria-live="polite">
            {page * 10 + 1}–{Math.min(users.length, page * 10 + 10)} of {users.length}
          </span>
          <button disabled={(page + 1) * 10 >= users.length} onClick={() => setPage((p) => p + 1)}>
            Next
          </button>
        </nav>
      )}
      {onBack && <button onClick={onBack}>Back to menu</button>}
    </section>
  );
}
export function RecommendPanel({ onBack }: { onBack: () => void }) {
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  return (
    <section className="account-panel">
      <h2>Invite a rival</h2>
      <p>
        Recommend RPG-Arena to a friend. We’ll send a one-time invitation with a link to the game
        and your display name.
      </p>
      <p className="hint">
        Sign in with a confirmed account first. Up to five invitations per day.
      </p>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          const form = e.currentTarget;
          const email = new FormData(form).get('email');
          setBusy(true);
          setError('');
          setMessage('');
          void accountRequest<{ message: string }>('recommend', { email })
            .then((r) => {
              setMessage(r.message);
              form.reset();
            })
            .catch((e) => setError(requestError(e)))
            .finally(() => setBusy(false));
        }}
      >
        <label>
          Friend’s email address
          <input type="email" name="email" required maxLength={254} />
        </label>
        <label className="consent">
          <input type="checkbox" required /> I know this person and they would welcome this
          invitation.
        </label>
        <button className="primary" disabled={busy}>
          {busy ? 'Sending…' : 'Send recommendation'}
        </button>
      </form>
      {message && <p role="status">{message}</p>}
      {error && <p role="alert">{error}</p>}
      <button onClick={onBack}>Back to menu</button>
    </section>
  );
}
